import { isEmpty, isNil } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import CollapsedFundList from 'src/components/Buy/BuyFlow/CollapsedFundList';
import Review from 'src/components/Buy/BuyFlow/Review';
import FundingSource from 'src/components/FundingSource/FundingSource/FundingSource';
import ProgressStep from 'src/components/ProgressStep/ProgressStep';
import AppTitle from 'src/components/Shared/AppTitle/AppTitle';
import { moduleActions } from 'src/redux/modules';
import { isSalesforceRep } from '../../../redux/modules/authentication/authentication.module.selectors';
import { isCustomerWebViewEnabled } from 'src/redux/modules/authentication/authentication.module.selectors';
import { getSelectedBank } from 'src/redux/modules/bankSelection/bankSelection.selectors';
import {
  getEligibleBanks,
  getSingleBankWithVerifyMicroDeposit,
} from 'src/redux/modules/existingBanks/existingBanks.selectors';
import { getDisplayTradeDate } from 'src/redux/modules/funds/UserFunds/userFunds.selectors';
import { CANCEL_TRADE_CONFIRMATION_MODAL } from 'src/redux/modules/modals/modals.module';
import {
  getCollegeAvailableFundList,
  getCollegeSelectedFundsError,
} from 'src/redux/modules/shoppingCart/collegeShoppingCart.selectors';
import {
  getFlattenedPurchasingFunds,
  getPurchasingFunds,
  getShoppingCartTotal,
  hasStopTransferFunds,
} from 'src/redux/modules/shoppingCart/shoppingCart.selectors';
import { formatUnixDate } from 'src/utils/dates/date-utility';
import { scrollElementIntoFullView } from 'src/utils/dom-utils';
import CollegeContributeFundList from '../CollegeContributeFundList/CollegeContributeFundList';
import './CollegeContributeSteps.scss';

const CollegeContributeSteps = ({
  actions,
  availableFunds,
  currentStep,
  customerWebViewEnabled,
  dispatchPageLoad,
  displayTradeDate,
  editModeBank,
  eligibleBanks,
  existingBanks: { error, fetchingAccounts: banksLoading, isLoaded },
  executeCollegeContribute,
  flattenedSelectedFunds,
  hasSelectedFundsError,
  isSalesforceRep,
  paperlessSetting,
  purchasingFunds,
  selectedBank,
  setProgress,
  shoppingCartTotal,
  singleBankWithVerifyMicroDeposit,
  stopTransfer,
  userFunds,
}) => {
  const { openModal } = actions;
  const formattedDisplayTradeDate = displayTradeDate
    ? formatUnixDate(displayTradeDate)
    : '';
  return (
    <form className="animated fadeInUp" onSubmit={(e) => e.preventDefault()}>
      <AppTitle
        subtitle="Our three-step process makes it easy to add to an existing portfolio or to invest in new portfolios."
        title="Contribute to Your 529 Plan"
      />

      <ProgressStep
        expanded
        active={currentStep === 1}
        className="college-funding-source-step step1"
        complete={currentStep > 1}
        nextButtonClassName="e2e-step1-verify-submit"
        nextButtonDisabled={
          isNil(selectedBank) || singleBankWithVerifyMicroDeposit
        }
        nextButtonLabel={
          <span>
            <b>NEXT:</b> Select Portfolios & Amounts
          </span>
        }
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRep)}
        step={1}
        title={
          <span>
            Choose{' '}
            <span className="college-contribute-steps-expanded-title">
              {' '}
              your
            </span>{' '}
            funding source
          </span>
        }
        onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
        onNextClick={() => setProgress(2)}
      >
        <FundingSource
          actions={actions}
          active={currentStep === 1}
          completed={currentStep > 1}
          dispatchPageLoad={dispatchPageLoad}
          editModeBank={editModeBank}
          error={error}
          fundingSource={eligibleBanks}
          isLoaded={isLoaded}
          isLoading={banksLoading}
          openDrawer={() => setProgress(1)}
          selectedBank={selectedBank}
          stopTransfer={stopTransfer}
        />
      </ProgressStep>

      <ProgressStep
        expanded
        active={currentStep === 2}
        className="college-contribute-step step2"
        complete={currentStep > 2}
        nextButtonDisabled={isEmpty(purchasingFunds) || hasSelectedFundsError}
        nextButtonLabel={
          <span>
            <b>NEXT</b>: Review & Submit
          </span>
        }
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRep)}
        step={2}
        title={
          <span>
            Select portfolio(s) and{' '}
            <span className="college-contribute-steps-expanded-title">
              contribution{' '}
            </span>{' '}
            amounts
          </span>
        }
        onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
        onNextClick={() => setProgress(3)}
      >
        {currentStep === 2 ? (
          <CollegeContributeFundList currentStep={currentStep} />
        ) : purchasingFunds.length === 0 ? null : (
          <CollapsedFundList
            clickable
            shoppingCartLength={purchasingFunds.length}
            shoppingCartTotal={shoppingCartTotal}
            onEditClick={() => setProgress(2)}
          />
        )}
      </ProgressStep>

      <ProgressStep
        active={currentStep === 3}
        className="review-step step3"
        expanded={currentStep === 3}
        nextButtonDisabled={
          customerWebViewEnabled || executeCollegeContribute.isLoading
        }
        nextButtonLabel="SUBMIT"
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRep)}
        step={3}
        title={
          <span>
            Review <span className="visible-xs-inline">& </span>
            <span className="hidden-xs">and </span>submit{' '}
            <span className="hidden-xs"> your contribution</span>
          </span>
        }
        onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
        onNextClick={() => {
          scrollElementIntoFullView('college-page-container');
          actions.submitCollegeContribute(
            availableFunds,
            false, // paperless, ignored for college
            false, // prior, paperless, ignored for college
            purchasingFunds,
            selectedBank
          );
          actions.resetTransaction();
        }}
      >
        <Review
          actions={actions}
          dispatchPageLoad={dispatchPageLoad}
          displayTradeDate={formattedDisplayTradeDate}
          flattenedPurchasingFunds={flattenedSelectedFunds}
          paperlessSetting={paperlessSetting}
          selectedBank={selectedBank}
          shoppingCartTotal={shoppingCartTotal}
          userFunds={userFunds}
        />
      </ProgressStep>
    </form>
  );
};

const mapStateToProps = (state) => {
  const {
    dispatchPageLoad,
    editModeBank,
    existingBanks,
    executeCollegeContribute,
    paperlessSetting,
    progress: { currentStep },
    userFunds,
  } = state;

  return {
    isSalesforceRep: isSalesforceRep(state),
    customerWebViewEnabled: isCustomerWebViewEnabled(state),
    availableFunds: getCollegeAvailableFundList(state),
    currentStep,
    displayTradeDate: getDisplayTradeDate(state),
    dispatchPageLoad,
    editModeBank,
    eligibleBanks: getEligibleBanks(state),
    executeCollegeContribute,
    existingBanks,
    hasSelectedFundsError: getCollegeSelectedFundsError(state),
    flattenedSelectedFunds: getFlattenedPurchasingFunds(state),
    paperlessSetting,
    purchasingFunds: getPurchasingFunds(state),
    selectedBank: getSelectedBank(state),
    shoppingCartTotal: getShoppingCartTotal(state),
    singleBankWithVerifyMicroDeposit:
      getSingleBankWithVerifyMicroDeposit(state),
    stopTransfer: hasStopTransferFunds(state),
    userFunds,
  };
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(moduleActions, dispatch),
  setProgress: (step) => {
    scrollElementIntoFullView(`step${step}`);
    dispatch(moduleActions.setProgress(step));
  },
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  CollegeContributeSteps
);
